// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FileText, CheckCircle, XCircle, Download, Loader2, AlertTriangle, BookOpen } from "lucide-react"
import { type DatasourceBillVersion } from '@/types/datasource-bill'
import { type ProcessedAmendmentData } from './types'

interface VersionDetailsCardProps {
  version: DatasourceBillVersion
  versionAmendmentData?: ProcessedAmendmentData
  onViewFullText: (version: DatasourceBillVersion) => void
  onDownloadPdf: (version: DatasourceBillVersion) => void
  onViewAmendments: (versionNumber: number, switchToTab: boolean) => void
  isDownloadPending: boolean
  isAmendmentsPending: boolean
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return '—'
  }
}

const getAmendmentStatus = (version: DatasourceBillVersion): boolean | null => {
  return version.amendments_detected
}

const getVersionBadge = (version: DatasourceBillVersion) => {
  const hasText = version.text_extracted && version.text_s3_key
  const amendmentStatus = getAmendmentStatus(version)
  
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="font-mono">
        V{version.version}
      </Badge>
      
      {hasText && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Text
        </Badge>
      )}
      
      {amendmentStatus === true && (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Amendments
        </Badge>
      )}
      
      {amendmentStatus === false && (
        <Badge variant="outline" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Clean
        </Badge>
      )}
    </div>
  )
}

export function VersionDetailsCard({ 
  version, 
  versionAmendmentData, 
  onViewFullText, 
  onDownloadPdf, 
  onViewAmendments,
  isDownloadPending,
  isAmendmentsPending
}: VersionDetailsCardProps) {
  const amendmentStatus = getAmendmentStatus(version)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-3">
          Version {version.version} Details
          {getVersionBadge(version)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Version Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="font-medium text-muted-foreground mb-3">Basic Information</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Created At:</span>
                <span className="font-mono text-xs">
                  {formatDate(version.created_at)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>MD5 Hash:</span>
                <span className="font-mono text-xs truncate max-w-[150px]" title={version.md5_hash}>
                  {version.md5_hash}
                </span>
              </div>
              {version.textract_job_id && (
                <div className="flex items-center justify-between">
                  <span>Textract Job ID:</span>
                  <span className="font-mono text-xs truncate max-w-[150px]" title={version.textract_job_id}>
                    {version.textract_job_id}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="font-medium text-muted-foreground mb-3">Processing Status</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Text Extracted</span>
                {version.text_extracted ? (
                  <Badge variant="default" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Yes
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    No
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Formatting Available</span>
                {version.formatting_s3_key ? (
                  <Badge variant="default" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Yes
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    No
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Amendment Status</span>
                {amendmentStatus === true ? (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Has Amendments
                  </Badge>
                ) : amendmentStatus === false ? (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Clean
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    {version.formatting_s3_key ? "Not Analyzed" : "No Formatting Data"}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* File Information */}
        <div>
          <div className="font-medium text-muted-foreground mb-3">File Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {version.text_s3_key && (
              <div>
                <span className="font-medium">Text S3 Key:</span>
                <div className="font-mono text-xs text-muted-foreground mt-1 break-all">
                  {version.text_s3_key}
                </div>
              </div>
            )}
            {version.pdf_s3_key && (
              <div>
                <span className="font-medium">PDF S3 Key:</span>
                <div className="font-mono text-xs text-muted-foreground mt-1 break-all">
                  {version.pdf_s3_key}
                </div>
              </div>
            )}
            {version.formatting_s3_key && (
              <div>
                <span className="font-medium">Formatting S3 Key:</span>
                <div className="font-mono text-xs text-muted-foreground mt-1 break-all">
                  {version.formatting_s3_key}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Amendment Analysis Details */}
        {versionAmendmentData && (
          <div>
            <div className="font-medium text-muted-foreground mb-3">Amendment Analysis</div>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">
                    {versionAmendmentData.summary.total_amendments}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Amendments</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-red-600">
                    {versionAmendmentData.summary.strikeout_count}
                  </div>
                  <div className="text-xs text-muted-foreground">Matter Stricken</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">
                    {versionAmendmentData.summary.underline_count}
                  </div>
                  <div className="text-xs text-muted-foreground">Amendments to Bill</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-orange-600">
                    {versionAmendmentData.summary.italics_count}
                  </div>
                  <div className="text-xs text-muted-foreground">Chamber/Committee</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-purple-600">
                    {versionAmendmentData.summary.pages_with_amendments}
                  </div>
                  <div className="text-xs text-muted-foreground">Pages Affected</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2 flex-wrap">
            {version.text_s3_key && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={() => onViewFullText(version)}
              >
                <FileText className="h-3 w-3" />
                View Full Text
              </Button>
            )}
            {version.pdf_s3_key && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={() => onDownloadPdf(version)}
                disabled={isDownloadPending}
              >
                {isDownloadPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
                Download PDF
              </Button>
            )}
            <Button 
              variant={version.amendments_detected ? "default" : "outline"} 
              size="sm" 
              className="flex items-center gap-1"
              onClick={() => {
                onViewAmendments(version.version, true) // Pass true to switch to tab
              }}
              disabled={isAmendmentsPending}
            >
              {isAmendmentsPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <BookOpen className="h-3 w-3" />
              )}
              {version.amendments_detected ? "View Amendments" : "View Amendment Status"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
